// pages/AdminUsers.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  Grid,
  Card,
  CardContent,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  TablePagination,
  Skeleton,
  Fade,
  Pagination,
  Stack,
  CircularProgress,
  Avatar,
  Badge,
  Divider,
  LinearProgress,
  useMediaQuery,
  useTheme,
  SwipeableDrawer,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Phone as PhoneIcon,
  Verified as VerifiedIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  PointOfSale as CasherIcon,
  PersonOutline as UserIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import debounce from 'lodash/debounce';
import api from '../services/api';

const AdminUsers = ({ keywordsData, language }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedMobileUser, setSelectedMobileUser] = useState(null);
  
  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Filter states
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [verificationFilter, setVerificationFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Custom confirmation states
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({});
  const [actionCallback, setActionCallback] = useState(null);
  
  // Export loading state
  const [exporting, setExporting] = useState(false);

  const [userForm, setUserForm] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user',
    wallet_id: '',
    points: 0,
    wallet_balance: 0,
    age: '',
    sex: '',
    user_phone: ''
  });

  const activeTxt = language === 'ar' ? 'نشط' : 'Active';
  const inActiveTxt = language === 'ar' ? 'غير نشط' : 'Inactive';
  const creatUserBtnTxt = language === 'ar' ? 'إنشاء' : 'Create';
  const updateUserBtnTxt = language === 'ar' ? 'تحديث' : 'Update';
  const creatUserTitleTxt = language === 'ar' ? 'إضافة مستخدم جديد' : 'Create new user';
  const updateUserTitleTxt = language === 'ar' ? 'تعديل المستخدم' : 'Update user';
  const verifiedTxt = language === 'ar' ? 'موثق' : 'Verified';
  const notVerifiedTxt = language === 'ar' ? 'غير موثق' : 'Not Verified';

  // Fetch users with pagination and filters
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm,
        role: roleFilter !== 'all' ? roleFilter : undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        verified: verificationFilter !== 'all' ? verificationFilter : undefined
      };
      
      const response = await api.get('/adminusers', { params });
      setUsers(response.data.users);
      setTotalUsers(response.data.pagination.total);
      setTotalPages(response.data.pagination.totalPages);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to fetch users' });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, roleFilter, statusFilter, verificationFilter]);

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchTerm(value);
      setPage(0);
    }, 500),
    []
  );

  const handleSearchChange = (e) => {
    debouncedSearch(e.target.value);
  };

  // Handle filter changes
  const handleRoleFilterChange = (e) => {
    setRoleFilter(e.target.value);
    setPage(0);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setPage(0);
  };

  const handleVerificationFilterChange = (e) => {
    setVerificationFilter(e.target.value);
    setPage(0);
  };

  const clearFilters = () => {
    setRoleFilter('all');
    setStatusFilter('all');
    setVerificationFilter('all');
    setSearchTerm('');
    setPage(0);
    if (isMobile) setShowFilters(false);
  };

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Custom confirmation function
  const showConfirm = (config, callback) => {
    setConfirmConfig(config);
    setActionCallback(() => callback);
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    setConfirmOpen(false);
    if (actionCallback) {
      actionCallback();
    }
    setActionCallback(null);
  };

  const handleCancel = () => {
    setConfirmOpen(false);
    setActionCallback(null);
  };

  // Handle form input changes
  const handleInputChange = (field) => (e) => {
    setUserForm(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  // Open dialog for new user
  const handleNewUser = () => {
    setUserForm({
      username: '',
      email: '',
      password: '',
      role: 'user',
      wallet_id: '',
      points: 0,
      wallet_balance: 0,
      age: '',
      sex: '',
      user_phone: ''
    });
    setEditingUser(null);
    setDialogOpen(true);
  };

  // Open dialog for editing user
  const handleEditUser = (user) => {
    setUserForm({
      username: user.username,
      email: user.email,
      password: '',
      role: user.role,
      wallet_id: user.wallet_id,
      points: user.points,
      wallet_balance: user.walletBalance,
      age: user.age || '',
      sex: user.sex || '',
      user_phone: user.user_phone || ''
    });
    setEditingUser(user);
    setDialogOpen(true);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await api.put(`/adminusers/edit/${editingUser.id}`, userForm);
        setMessage({ type: 'success', text: 'User updated successfully!' });
      } else {
        await api.post('/adminusers/add', userForm);
        setMessage({ type: 'success', text: 'User created successfully!' });
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Operation failed' 
      });
    }
  };

  // Handle delete user
  const handleDeleteUser = (user) => {
    showConfirm(
      {
        title: language === 'ar' ? 'تأكيد الحذف' : 'Confirm Delete',
        description: language === 'ar' ? `هل أنت متأكد من رغبتك في حذف المستخدم "${user.username}"؟ لا يمكن التراجع عن هذا الإجراء.` : `Are you sure you want to delete user "${user.username}"? This action cannot be undone.`,
        confirmText: language === 'ar' ? 'حذف' : 'Delete',
        cancelText: language === 'ar' ? 'إلغاء' : 'Cancel'
      },
      async () => {
        try {
          await api.delete(`/adminusers/delete/${user.id}`);
          setMessage({ type: 'success', text: 'User deleted successfully!' });
          fetchUsers();
        } catch (error) {
          console.error('Delete error:', error);
          setMessage({ type: 'error', text: 'Failed to delete user' });
        }
      }
    );
  };

  // Handle toggle phone verification
  const handleTogglePhoneVerification = async (user) => {
    const newStatus = !user.is_phone_verified;
    
    showConfirm(
      {
        title: newStatus ? (language === 'ar' ? 'تأكيد التوثيق' : 'Confirm Verification') : (language === 'ar' ? 'إلغاء التوثيق' : 'Unverify Phone'),
        description: newStatus
          ? (language === 'ar' 
              ? `هل أنت متأكد من توثيق رقم الهاتف للمستخدم "${user.username}"؟`
              : `Are you sure you want to verify phone for user "${user.username}"?`)
          : (language === 'ar'
              ? `هل أنت متأكد من إلغاء توثيق رقم الهاتف للمستخدم "${user.username}"؟`
              : `Are you sure you want to unverify phone for user "${user.username}"?`),
        confirmText: language === 'ar' ? 'تأكيد' : 'Confirm',
        cancelText: language === 'ar' ? 'إلغاء' : 'Cancel'
      },
      async () => {
        try {
          await api.post(`/adminusers/toggle-phone-verification/${user.id}`);
          setMessage({ 
            type: 'success', 
            text: newStatus 
              ? (language === 'ar' ? 'تم توثيق رقم الهاتف بنجاح' : 'Phone verified successfully')
              : (language === 'ar' ? 'تم إلغاء توثيق رقم الهاتف' : 'Phone verification removed')
          });
          fetchUsers();
        } catch (error) {
          setMessage({ 
            type: 'error', 
            text: error.response?.data?.message || 'Failed to update phone verification' 
          });
        }
      }
    );
  };

  // Export users to CSV
  const handleExportUsers = async () => {
    setExporting(true);
    try {
      const response = await api.get('/adminusers/export', {
        params: {
          search: searchTerm,
          role: roleFilter !== 'all' ? roleFilter : undefined,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          verified: verificationFilter !== 'all' ? verificationFilter : undefined
        },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `users_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setMessage({ type: 'success', text: 'Users exported successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to export users' });
    } finally {
      setExporting(false);
    }
  };

  // Get role icon
  const getRoleIcon = (role) => {
    switch(role) {
      case 'admin': return <AdminIcon fontSize="small" />;
      case 'casher': return <CasherIcon fontSize="small" />;
      default: return <UserIcon fontSize="small" />;
    }
  };

  // Get role color
  const getRoleColor = (role) => {
    switch(role) {
      case 'admin': return '#F44336';
      case 'casher': return '#FF9800';
      default: return '#4CAF50';
    }
  };

  // Mobile User Card Component
  const UserCard = ({ user }) => (
    <Card sx={{
      backgroundColor: '#1A1A1A',
      border: '1px solid rgba(255, 215, 0, 0.15)',
      borderRadius: '16px',
      mb: 2,
      '&:hover': {
        borderColor: '#FFD700',
        transform: 'translateY(-2px)',
        transition: 'all 0.2s ease'
      }
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ width: 48, height: 48, backgroundColor: getRoleColor(user.role), fontSize: '1.2rem' }}>
              {user.username?.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 700 }}>
                {user.username}
              </Typography>
              <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
                {user.email}
              </Typography>
            </Box>
          </Box>
          <Chip
            icon={getRoleIcon(user.role)}
            label={user.role}
            size="small"
            sx={{ backgroundColor: getRoleColor(user.role), color: '#FFFFFF', fontWeight: 600 }}
          />
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,215,0,0.1)', my: 1.5 }} />

        <Grid container spacing={1.5}>
          <Grid item xs={6}>
            <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
              {language === 'ar' ? 'رقم المحفظة' : 'Wallet ID'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#FFF', fontFamily: 'monospace' }}>
              {user.wallet_id}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
              {language === 'ar' ? 'رقم الهاتف' : 'Phone'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#FFF' }}>
              {user.user_phone || 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
              {language === 'ar' ? 'العمر/الجنس' : 'Age/Sex'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#FFF' }}>
              {user.age ? `${user.age}y` : 'N/A'} / {user.sex || 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
              {language === 'ar' ? 'النقاط' : 'Points'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#FFF', fontWeight: 'bold' }}>
              {user.points.toLocaleString()}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
              {language === 'ar' ? 'الرصيد' : 'Balance'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#FFD700', fontWeight: 'bold' }}>
              {user.walletBalance.toFixed(2)} $
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
              {language === 'ar' ? 'الحالة' : 'Status'}
            </Typography>
            <Chip
              label={user.is_active ? activeTxt : inActiveTxt}
              color={user.is_active ? 'success' : 'default'}
              size="small"
              sx={{ mt: 0.5 }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" sx={{ color: '#B3B3B3' }}>
              {language === 'ar' ? 'توثيق الهاتف' : 'Phone Verification'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Chip
                icon={user.is_phone_verified ? <VerifiedIcon /> : <PhoneIcon />}
                label={user.is_phone_verified ? verifiedTxt : notVerifiedTxt}
                size="small"
                sx={{
                  backgroundColor: user.is_phone_verified ? '#4CAF50' : '#FF9800',
                  color: '#FFFFFF',
                  fontWeight: 600
                }}
              />
              <IconButton 
                size="small" 
                onClick={() => handleTogglePhoneVerification(user)} 
                sx={{ color: user.is_phone_verified ? '#FF9800' : '#4CAF50' }}
              >
                {user.is_phone_verified ? <RefreshIcon /> : <CheckCircleIcon />}
              </IconButton>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
          <Button
            size="small"
            disabled = {user.is_oregnal_admin} 
            startIcon={<EditIcon />}
            onClick={() => handleEditUser(user)}
            sx={{
              color: '#FFD700',
              borderColor: '#FFD700',
              '&:hover': { backgroundColor: 'rgba(255, 215, 0, 0.1)' }
            }}
            variant="outlined"
          >
            {language === 'ar' ? 'تعديل' : 'Edit'}
          </Button>
          <Button
            size="small"
            disabled = {user.is_oregnal_admin} 
            startIcon={<DeleteIcon />}
            onClick={() => handleDeleteUser(user)}
            sx={{
              color: '#F44336',
              borderColor: '#F44336',
              '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.1)' }
            }}
            variant="outlined"
          >
            {language === 'ar' ? 'حذف' : 'Delete'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  // Skeleton loader for mobile
  const MobileSkeleton = () => (
    <>
      {[...Array(5)].map((_, i) => (
        <Card key={i} sx={{ backgroundColor: '#1A1A1A', mb: 2, p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Skeleton variant="circular" width={48} height={48} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="80%" />
            </Box>
          </Box>
          <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
        </Card>
      ))}
    </>
  );

  // Skeleton loader for desktop
  const TableSkeleton = () => (
    <>
      {[...Array(rowsPerPage)].map((_, i) => (
        <TableRow key={i}>
          <TableCell><Skeleton variant="text" width={150} /></TableCell>
          <TableCell><Skeleton variant="text" width={100} /></TableCell>
          <TableCell><Skeleton variant="text" width={80} /></TableCell>
          <TableCell><Skeleton variant="text" width={120} /></TableCell>
          <TableCell><Skeleton variant="text" width={100} /></TableCell>
          <TableCell><Skeleton variant="text" width={80} /></TableCell>
          <TableCell><Skeleton variant="text" width={60} /></TableCell>
          <TableCell><Skeleton variant="text" width={80} /></TableCell>
          <TableCell><Skeleton variant="text" width={70} /></TableCell>
          <TableCell><Skeleton variant="text" width={100} /></TableCell>
        </TableRow>
      ))}
    </>
  );

  // Filters Component
  const FiltersSection = () => (
    <Paper sx={{ p: 2, mb: 3, backgroundColor: '#0D0D0D', borderRadius: '16px', border: '1px solid rgba(255,215,0,0.15)' }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            size="small"
            placeholder={language === 'ar' ? 'ابحث عن مستخدم...' : 'Search users...'}
            onChange={handleSearchChange}
            defaultValue={searchTerm}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#888888' }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => { setSearchTerm(''); setPage(0); }}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
              sx: { backgroundColor: '#1A1A1A', borderRadius: '10px' }
            }}
          />
        </Grid>
        {!isMobile && (
          <>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: '#B3B3B3' }}>Role</InputLabel>
                <Select value={roleFilter} onChange={handleRoleFilterChange} label="Role" sx={{ backgroundColor: '#1A1A1A', borderRadius: '10px', color: '#FFF' }}>
                  <MenuItem value="all">All Roles</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="casher">Casher</MenuItem>
                  <MenuItem value="user">User</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: '#B3B3B3' }}>Status</InputLabel>
                <Select value={statusFilter} onChange={handleStatusFilterChange} label="Status" sx={{ backgroundColor: '#1A1A1A', borderRadius: '10px', color: '#FFF' }}>
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: '#B3B3B3' }}>Verified</InputLabel>
                <Select value={verificationFilter} onChange={handleVerificationFilterChange} label="Verified" sx={{ backgroundColor: '#1A1A1A', borderRadius: '10px', color: '#FFF' }}>
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="true">Verified</MenuItem>
                  <MenuItem value="false">Not Verified</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                sx={{ borderRadius: '10px', textTransform: 'none', borderColor: '#FFD700', color: '#FFD700' }}
              >
                Clear Filters
              </Button>
            </Grid>
          </>
        )}
        {isMobile && (
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setShowFilters(!showFilters)}
              sx={{ borderRadius: '10px', textTransform: 'none', borderColor: '#FFD700', color: '#FFD700' }}
            >
              {showFilters ? (language === 'ar' ? 'إخفاء الفلاتر' : 'Hide Filters') : (language === 'ar' ? 'إظهار الفلاتر' : 'Show Filters')}
            </Button>
          </Grid>
        )}
      </Grid>

      {/* Mobile Filters Expandable Section */}
      {isMobile && showFilters && (
        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255,215,0,0.1)' }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: '#B3B3B3' }}>Role</InputLabel>
                <Select value={roleFilter} onChange={handleRoleFilterChange} label="Role" sx={{ backgroundColor: '#1A1A1A', borderRadius: '10px', color: '#FFF' }}>
                  <MenuItem value="all">All Roles</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="casher">Casher</MenuItem>
                  <MenuItem value="user">User</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: '#B3B3B3' }}>Status</InputLabel>
                <Select value={statusFilter} onChange={handleStatusFilterChange} label="Status" sx={{ backgroundColor: '#1A1A1A', borderRadius: '10px', color: '#FFF' }}>
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: '#B3B3B3' }}>Verified</InputLabel>
                <Select value={verificationFilter} onChange={handleVerificationFilterChange} label="Verified" sx={{ backgroundColor: '#1A1A1A', borderRadius: '10px', color: '#FFF' }}>
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="true">Verified</MenuItem>
                  <MenuItem value="false">Not Verified</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                sx={{ borderRadius: '10px', textTransform: 'none', borderColor: '#FFD700', color: '#FFD700' }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}
    </Paper>
  );

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 }, mb: { xs: 8, sm: 10 }, minHeight: '100vh' }}>
      {/* Header with Animation */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', sm: 'center' }, 
          mb: { xs: 3, sm: 4 }, 
          gap: 2 
        }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ 
              fontWeight: 800, 
              fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
              background: 'linear-gradient(135deg, #FFD700 0%, #FF8C00 100%)', 
              backgroundClip: 'text', 
              WebkitBackgroundClip: 'text', 
              WebkitTextFillColor: 'transparent' 
            }}>
              {language === 'ar' ? 'إدارة المستخدمين' : 'Users Management'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#888888', mt: 0.5 }}>
              {language === 'ar' ? 'إدارة حسابات المستخدمين والصلاحيات' : 'Manage user accounts and permissions'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Button
              variant="contained"
              startIcon={exporting ? <CircularProgress size={20} /> : <DownloadIcon />}
              onClick={handleExportUsers}
              disabled={exporting}
              fullWidth={isMobile}
              sx={{ 
                borderRadius: '12px', 
                textTransform: 'none',
                backgroundColor: '#2D2D2D',
                '&:hover': { backgroundColor: '#3D3D3D' }
              }}
            >
              {language === 'ar' ? 'تصدير' : 'Export'}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleNewUser}
              fullWidth={isMobile}
              sx={{
                background: 'linear-gradient(135deg, #FFD700 0%, #F5C800 100%)',
                color: '#000000',
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 700,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(255,215,0,0.4)'
                }
              }}
            >
              {language === 'ar' ? 'إضافة مستخدم' : 'Add New User'}
            </Button>
          </Box>
        </Box>
      </motion.div>

      {/* Message Alert */}
      {message.text && (
        <Fade in={!!message.text}>
          <Alert 
            severity={message.type} 
            sx={{ mb: 3, borderRadius: '12px' }} 
            onClose={() => setMessage({ type: '', text: '' })}
          >
            {message.text}
          </Alert>
        </Fade>
      )}

      {/* Stats Cards - Responsive Grid */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={6} md={3}>
          <motion.div whileHover={{ y: -4 }} transition={{ duration: 0.2 }}>
            <Card sx={{ backgroundColor: '#0D0D0D', border: '1px solid rgba(255,215,0,0.15)', borderRadius: '16px' }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="body2" color="#888888" gutterBottom sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  {language === 'ar' ? 'إجمالي المستخدمين' : 'Total Users'}
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 800, color: '#FFD700', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' } }}>
                  {totalUsers.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <motion.div whileHover={{ y: -4 }} transition={{ duration: 0.2 }}>
            <Card sx={{ backgroundColor: '#0D0D0D', border: '1px solid rgba(255,215,0,0.15)', borderRadius: '16px' }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="body2" color="#888888" gutterBottom sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  {language === 'ar' ? 'المشرفين' : 'Admins'}
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 800, color: '#F44336', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' } }}>
                  {users?.filter(u => u.role === 'admin').length}
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <motion.div whileHover={{ y: -4 }} transition={{ duration: 0.2 }}>
            <Card sx={{ backgroundColor: '#0D0D0D', border: '1px solid rgba(255,215,0,0.15)', borderRadius: '16px' }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="body2" color="#888888" gutterBottom sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  {language === 'ar' ? 'الكاشير' : 'Cashers'}
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 800, color: '#FF9800', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' } }}>
                  {users?.filter(u => u.role === 'casher').length}
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <motion.div whileHover={{ y: -4 }} transition={{ duration: 0.2 }}>
            <Card sx={{ backgroundColor: '#0D0D0D', border: '1px solid rgba(255,215,0,0.15)', borderRadius: '16px' }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="body2" color="#888888" gutterBottom sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                  {language === 'ar' ? 'موثقين' : 'Verified'}
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 800, color: '#4CAF50', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' } }}>
                  {users?.filter(u => u.is_phone_verified).length}
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {/* Filters Section */}
      <FiltersSection />

      {/* Users List - Mobile Card View / Desktop Table View */}
      {isMobile ? (
        // Mobile Card View
        <Box>
          {loading ? (
            <MobileSkeleton />
          ) : users.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: '#0D0D0D', border: '1px solid #2D2D2D', borderRadius: '16px' }}>
              <PersonIcon sx={{ fontSize: 64, color: '#B3B3B3', mb: 2 }} />
              <Typography sx={{ color: '#B3B3B3' }}>
                {language === 'ar' ? 'لا توجد مستخدمين' : 'No users found'}
              </Typography>
            </Paper>
          ) : (
            users.map((user) => <UserCard key={user.id} user={user} />)
          )}
          
          {/* Pagination for Mobile */}
          {totalPages > 0 && !loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page + 1}
                onChange={(e, value) => setPage(value - 1)}
                color="primary"
                shape="rounded"
                size="small"
                sx={{
                  '& .MuiPaginationItem-root': {
                    color: '#FFFFFF',
                    borderColor: '#2D2D2D',
                    '&:hover': { backgroundColor: 'rgba(255,215,0,0.1)' },
                    '&.Mui-selected': {
                      backgroundColor: '#FFD700',
                      color: '#000000',
                      '&:hover': { backgroundColor: '#FFE55C' }
                    }
                  }
                }}
              />
            </Box>
          )}
        </Box>
      ) : (
        // Desktop Table View
        <>
          <TableContainer component={Paper} sx={{ borderRadius: '16px', overflow: 'hidden' }}>
            <Table>
              <TableHead sx={{ backgroundColor: '#1A1A1A' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: '#FFD700' }}>{language === 'ar' ? 'المستخدم' : 'User'}</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#FFD700' }}>{language === 'ar' ? 'رقم المحفظة' : 'Wallet ID'}</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#FFD700' }}>{language === 'ar' ? 'الرتبة' : 'Role'}</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#FFD700' }}>{language === 'ar' ? 'رقم الهاتف' : 'Phone'}</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#FFD700' }}>{language === 'ar' ? 'التوثيق' : 'Verified'}</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#FFD700' }}>{language === 'ar' ? 'العمر/الجنس' : 'Age/Sex'}</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#FFD700' }}>{language === 'ar' ? 'النقاط' : 'Points'}</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#FFD700' }}>{language === 'ar' ? 'الرصيد' : 'Balance'}</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#FFD700' }}>{language === 'ar' ? 'الحالة' : 'Status'}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: '#FFD700' }}>{language === 'ar' ? 'إجراءات' : 'Actions'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableSkeleton />
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <PersonIcon sx={{ fontSize: 64, color: '#2D2D2D', mb: 2 }} />
                        <Typography variant="h6" color="#888888">
                          {language === 'ar' ? 'لا توجد مستخدمين' : 'No users found'}
                        </Typography>
                        <Typography variant="body2" color="#666666">
                          {language === 'ar' ? 'حاول تعديل معايير البحث' : 'Try adjusting your search filters'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.id} hover sx={{ '&:hover': { backgroundColor: 'rgba(255,215,0,0.05)' } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ width: 32, height: 32, backgroundColor: getRoleColor(user.role), fontSize: '0.9rem' }}>
                            {user.username?.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#FFF' }}>{user.username}</Typography>
                            <Typography variant="caption" color="#888888">{user.email}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={user.wallet_id} size="small" variant="outlined" sx={{ fontFamily: 'monospace', color: '#FFF', borderColor: '#2D2D2D' }} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getRoleIcon(user.role)}
                          label={user.role}
                          size="small"
                          sx={{ backgroundColor: getRoleColor(user.role), color: '#FFFFFF', fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="#FFF">
                          {user.user_phone || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {user.user_phone ? (
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Chip
                              icon={user.is_phone_verified ? <VerifiedIcon /> : <PhoneIcon />}
                              label={user.is_phone_verified ? verifiedTxt : notVerifiedTxt}
                              size="small"
                              sx={{
                                backgroundColor: user.is_phone_verified ? '#4CAF50' : '#FF9800',
                                color: '#FFFFFF',
                                fontWeight: 600
                              }}
                            />
                            <Tooltip title={user.is_phone_verified ? (language === 'ar' ? 'إلغاء التوثيق' : 'Unverify') : (language === 'ar' ? 'توثيق' : 'Verify')}>
                              <IconButton size="small" onClick={() => handleTogglePhoneVerification(user)} sx={{ color: user.is_phone_verified ? '#FF9800' : '#4CAF50' }}>
                                {user.is_phone_verified ? <RefreshIcon /> : <CheckCircleIcon />}
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="#888888">No phone</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="#FFF">
                          {user.age ? `${user.age}y` : 'N/A'} / {user.sex || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium" color="#FFF">
                          {user.points.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold" color="#FFD700">
                          {user.walletBalance.toFixed(2)} $
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.is_active ? activeTxt : inActiveTxt}
                          color={user.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <IconButton disabled = {user.is_oregnal_admin} size="small" onClick={() => handleEditUser(user)} sx={{ color: '#FFD700' }}>
                            <EditIcon />
                          </IconButton>
                          <IconButton disabled = {user.is_oregnal_admin} size="small" onClick={() => handleDeleteUser(user)} sx={{ color: '#F44336' }}>
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination for Desktop */}
          {totalPages > 0 && !loading && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="body2" color="#888888">
                {language === 'ar' ? `عرض ${users.length} من ${totalUsers} مستخدم` : `Showing ${users.length} of ${totalUsers} users`}
              </Typography>
              <Pagination
                count={totalPages}
                page={page + 1}
                onChange={(e, value) => setPage(value - 1)}
                color="primary"
                shape="rounded"
                sx={{
                  '& .MuiPaginationItem-root': {
                    color: '#FFFFFF',
                    borderColor: '#2D2D2D',
                    '&:hover': { backgroundColor: 'rgba(255,215,0,0.1)' },
                    '&.Mui-selected': {
                      backgroundColor: '#FFD700',
                      color: '#000000',
                      '&:hover': { backgroundColor: '#FFE55C' }
                    }
                  }
                }}
              />
            </Box>
          )}
        </>
      )}

      {/* Add/Edit User Dialog - Responsive */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth 
        fullScreen={isMobile}
        dir={language === 'ar' ? 'rtl' : 'ltr'}
        PaperProps={{
          sx: {
            backgroundColor: '#0D0D0D',
            borderRadius: isMobile ? 0 : '16px',
            m: isMobile ? 0 : 2
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(255,215,0,0.1)', p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#FFD700' }}>
            {editingUser ? updateUserTitleTxt : creatUserTitleTxt}
          </Typography>
          {isMobile && (
            <IconButton
              onClick={() => setDialogOpen(false)}
              sx={{ position: 'absolute', right: 8, top: 8, color: '#B3B3B3' }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 2, p: { xs: 2, sm: 3 } }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={language === 'ar' ? 'إسم المستخدم' : 'Username'}
                  value={userForm.username}
                  onChange={handleInputChange('username')}
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: '#1A1A1A', '& fieldset': { borderColor: '#2D2D2D' } }, '& .MuiInputLabel-root': { color: '#B3B3B3' }, '& .MuiOutlinedInput-input': { color: '#FFF' } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={language === 'ar' ? 'الإيميل' : 'Email'}
                  type="email"
                  value={userForm.email}
                  onChange={handleInputChange('email')}
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: '#1A1A1A', '& fieldset': { borderColor: '#2D2D2D' } }, '& .MuiInputLabel-root': { color: '#B3B3B3' }, '& .MuiOutlinedInput-input': { color: '#FFF' } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={language === 'ar' ? 'كلمة المرور' : 'Password'}
                  type="password"
                  value={userForm.password}
                  onChange={handleInputChange('password')}
                  required={!editingUser}
                  helperText={editingUser ? (language === "ar" ? "اتركه فارغًا للاحتفاظ بكلمة المرور الحالية" : "Leave blank to keep current password") : ""}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: '#1A1A1A', '& fieldset': { borderColor: '#2D2D2D' } }, '& .MuiInputLabel-root': { color: '#B3B3B3' }, '& .MuiOutlinedInput-input': { color: '#FFF' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'الرتبة' : 'Role'}</InputLabel>
                  <Select value={userForm.role} label={language === 'ar' ? 'الرتبة' : 'Role'} onChange={handleInputChange('role')} required sx={{ borderRadius: '10px', backgroundColor: '#1A1A1A', color: '#FFF' }}>
                    <MenuItem value="user">User</MenuItem>
                    <MenuItem value="casher">Casher</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={language === 'ar' ? 'رقم معرف المحفظة' : 'Wallet ID'}
                  value={userForm.wallet_id}
                  onChange={handleInputChange('wallet_id')}
                  placeholder="6 digits"
                  required
                  inputProps={{ maxLength: 6 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: '#1A1A1A', '& fieldset': { borderColor: '#2D2D2D' } }, '& .MuiInputLabel-root': { color: '#B3B3B3' }, '& .MuiOutlinedInput-input': { color: '#FFF' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={language === 'ar' ? 'العمر' : 'Age'}
                  type="number"
                  value={userForm.age}
                  onChange={handleInputChange('age')}
                  inputProps={{ min: 0, max: 120 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: '#1A1A1A', '& fieldset': { borderColor: '#2D2D2D' } }, '& .MuiInputLabel-root': { color: '#B3B3B3' }, '& .MuiOutlinedInput-input': { color: '#FFF' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'الجنس' : 'Gender'}</InputLabel>
                  <Select value={userForm.sex} label={language === 'ar' ? 'الجنس' : 'Gender'} onChange={handleInputChange('sex')} sx={{ borderRadius: '10px', backgroundColor: '#1A1A1A', color: '#FFF' }}>
                    <MenuItem value="">{language === 'ar' ? 'اختر' : 'Select'}</MenuItem>
                    <MenuItem value="male">{language === 'ar' ? 'ذكر' : 'Male'}</MenuItem>
                    <MenuItem value="female">{language === 'ar' ? 'أنثى' : 'Female'}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={language === 'ar' ? 'رقم الهاتف' : 'Phone Number'}
                  value={userForm.user_phone}
                  onChange={handleInputChange('user_phone')}
                  placeholder="+201234567890"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: '#1A1A1A', '& fieldset': { borderColor: '#2D2D2D' } }, '& .MuiInputLabel-root': { color: '#B3B3B3' }, '& .MuiOutlinedInput-input': { color: '#FFF' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={language === 'ar' ? 'النقاط' : 'Points'}
                  type="number"
                  value={userForm.points}
                  onChange={handleInputChange('points')}
                  inputProps={{ min: 0 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: '#1A1A1A', '& fieldset': { borderColor: '#2D2D2D' } }, '& .MuiInputLabel-root': { color: '#B3B3B3' }, '& .MuiOutlinedInput-input': { color: '#FFF' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={language === 'ar' ? 'رصيد المحفظة' : 'Wallet Balance'}
                  type="number"
                  value={userForm.wallet_balance}
                  onChange={handleInputChange('wallet_balance')}
                  inputProps={{ step: "0.01", min: "0" }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: '#1A1A1A', '& fieldset': { borderColor: '#2D2D2D' } }, '& .MuiInputLabel-root': { color: '#B3B3B3' }, '& .MuiOutlinedInput-input': { color: '#FFF' } }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: { xs: 2, sm: 3 }, borderTop: '1px solid rgba(255,215,0,0.1)', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
            <Button onClick={() => setDialogOpen(false)} fullWidth={isMobile} sx={{ color: '#888888' }}>
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button type="submit" variant="contained" fullWidth={isMobile} sx={{ backgroundColor: '#FFD700', color: '#000000', '&:hover': { backgroundColor: '#FFE55C' } }}>
              {editingUser ? updateUserBtnTxt : creatUserBtnTxt}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Custom Confirmation Dialog - Responsive */}
      <Dialog 
        open={confirmOpen} 
        onClose={handleCancel} 
        fullWidth 
        fullScreen={isMobile}
        dir={language === 'ar' ? 'rtl' : 'ltr'}
        PaperProps={{
          sx: {
            backgroundColor: '#0D0D0D',
            borderRadius: isMobile ? 0 : '16px'
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(255,215,0,0.1)', color: '#FFD700' }}>{confirmConfig.title}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography sx={{ color: '#FFF' }}>{confirmConfig.description}</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
          <Button onClick={handleCancel} fullWidth={isMobile} sx={{ color: '#888888' }}>
            {confirmConfig.cancelText || (language === 'ar' ? 'إلغاء' : 'Cancel')}
          </Button>
          <Button onClick={handleConfirm} variant="contained" fullWidth={isMobile} sx={{ backgroundColor: '#F44336', '&:hover': { backgroundColor: '#D32F2F' } }}>
            {confirmConfig.confirmText || (language === 'ar' ? 'تأكيد' : 'Confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminUsers;